import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { Backdrop, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Confirm,
  FormDataConsumer,
  SaveButton,
  useNotify,
  useRedirect,
} from "react-admin";
import { useForm } from "react-final-form";

import {
  FilePaths,
  InboundState,
  InboundStatus,
  NoteType,
  PhotoStatus,
  WorkflowType,
} from "../../../utils/constants";
import { getCustomFieldsToSave } from "../../../components/common/CustomFields";
import { logError } from "../../../utils/appUtils";
import { saveRemoveFiles } from "../../../utils/fileUtils";
import { isBulkShipment } from "../../../utils/loadTypeUtils";
import { normalizeValue } from "../../../utils/utils";
import { validateAppointment } from "../../../utils/dateUtils";
import { CancelIcon } from "../../../components/common/iconography";
import { resolveCreateBillToAccountId } from "../accounts/AccountUtils";
import {
  autogenLotNumber,
  calcNetWeight,
  formValidate,
  SIT_LIST_PATH,
  validateInboundUnicity,
} from "./InboundUtils";
const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

export const ValidateCreateButton = ({
  account,
  operatingAccount,
  photos = [],
  selectedTimeslot,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [open, setOpen] = useState(false);
  const [confirmContent, setConfirmContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const isDisabled = photos?.some((p) => p.status === PhotoStatus.UPLOADING);
  const getEffectiveAccount = (data) =>
    operatingAccount || data?.account || account;

  const handleValidateSave = async (formData) => {
    const effectiveAccount = getEffectiveAccount(formData);
    const messages = [];
    debugger;

    const isValid = formValidate(form, notify);
    if (isValid) {
      const duplicateNumbers = await validateInboundUnicity(formData);
      if (duplicateNumbers.length > 0) {
        messages.push(
          "Inbound with this " +
            duplicateNumbers.join(" & ") +
            "Number already exists. Saving will create a possible duplicate.",
        );
      }
      if (
        effectiveAccount?.trackInboundSeals &&
        !formData?.sealNumbers?.length
      ) {
        messages.push(
          "No seal numbers were added. Proceed saving without tracking seals?",
        );
      }
      if (selectedTimeslot) {
        try {
          const isBooked = await validateAppointment(selectedTimeslot);
          if (isBooked) {
            notify(
              `${selectedTimeslot.time} is no longer available. Please select a new time.`,
              "warning",
            );
            return;
          }
        } catch (error) {
          logError(
            "inboundShipments:ValidateCreateButton:handleValidateSave",
            error,
            notify,
          );
          return;
        }
      }
    }

    if (messages.length) {
      const messageFragment = (
        <>
          {messages.map((msg, index) => (
            <React.Fragment key={index}>
              {msg} <br />
            </React.Fragment>
          ))}
        </>
      );
      setConfirmContent(messageFragment);
      setOpen(true);
    } else handleSave();
  };

  const handleDialogClose = () => setOpen(false);
  const handleDialogConfirm = () => handleSave();

  const handleSave = () => {
    setOpen(false);
    props.handleSubmitWithRedirect();
  };

  const transform = async (data) => {
    const effectiveAccount = getEffectiveAccount(data);
    setIsSaving(true);
    data = { ...data };
    debugger;

    // Parent identity minted client-side; AppSync no longer creates the record.
    data.id = uuidv4();
    data.owner = window.appUser.owner;
    data.createdAt = new Date().toISOString();
    data.updatedAt = data.createdAt;

    try {
      // SIT shipments hide these options in the form; enforce false on save.
      if (data.sit) {
        data.secondaryReview = false;
        data.stockImport = false;
        data.containerDrop = false;
      }

      data.active = "true";
      data.status = data.sit
        ? InboundStatus.SIT_PENDING
        : data.stockImport
          ? InboundStatus.PLACED
          : InboundStatus.IN_TRANSIT;
      data.state = data.stockImport
        ? InboundState.PROCESS_INVENTORY
        : InboundState.NEW;
      data.groups = window.appUser?.groups;
      data.reloadYardId = window.appUser?.reloadYardId;
      data.accountName = account.businessName;
      // Resolved from the account type, not the form: the form value is only
      // honoured for INTERNAL accounts (see resolveCreateBillToAccountId).
      data.billToAccountId = resolveCreateBillToAccountId(
        account,
        data.billToAccountId,
      );
      data.railCarNumber = data.railCarNumber ? data.railCarNumber : "";
      data.invoiceId = " "; // required for secondary index

      data.createDateTime = new Date();
      data.scheduledDateTime = data.scheduledDateTime
        ? data.scheduledDateTime
        : null;

      // Mark Stock Import Inbound as Placed
      // if all inventories are located, mark as released
      data.notifyDateTime = data.stockImport ? new Date() : null;
      data.placedDateTime = data.stockImport ? new Date() : null;
      data.startDateTime = data.stockImport ? new Date() : null;
      data.releaseDateTime = null;
      data.daysOnDemurrageClock = 0;

      const allInventoriesLocated =
        data.stockImport &&
        (data.inventories?.items || []).length > 0 &&
        (data.inventories?.items || []).every((inv) => inv.locationFullName);
      if (allInventoriesLocated) {
        data.status = InboundStatus.RELEASED;
        data.state = InboundState.COMPLETED;
        data.releaseDateTime = data.placedDateTime;
        data.endDateTime = data.placedDateTime;
      }

      data.lotNumber =
        data.lotNumberOverride === true
          ? data.lotNumber
          : autogenLotNumber(data.lotType, data, effectiveAccount);

      data.lotType = data.lotType ? data.lotType : "";
      data.scaleWeightUnits = data.weightUnits;
      data.shipFrom = data.shipFrom ? data.shipFrom : data.accountName;
      data.poNumber = data.poNumber ? data.poNumber : "";
      data.normalizedPoNumber = normalizeValue(data.poNumber);
      data.normalizedRailCarNumber = normalizeValue(data.railCarNumber);
      data.zoneId = data.zoneId || " ";

      // if netWeight not calculated in form, try it here
      const hasWeights = data.inventories?.items?.every(
        (itm) => itm.weight != null && !isNaN(itm.weight),
      );
      if (hasWeights) {
        const weghtUnits =
          data.weightUnits ||
          data.inventories.items[0]?.weightUnits ||
          data.inventories.items[0]?.weightUOM;
        data.netWeight = calcNetWeight(data, null, weghtUnits);
        if (data.netWeight) data.weightUnits = weghtUnits;
      }

      if (isBulkShipment(data.reloadYardCommodityId, data.areaId))
        data.workflowType = WorkflowType.BULK;
      else data.workflowType = WorkflowType.ACCEPT_STORE;

      data.customFields = getCustomFieldsToSave(
        data.customFields,
        effectiveAccount?.inboundCustomFields,
      );

      data.history = [
        {
          text: `Created Inbound.`,
          type: NoteType.GENERIC,
          date: new Date(),
          by: window.appUser?.fullName,
        },
      ];

      if (data.notes?.length > 0) {
        data.notes.forEach((element) => {
          element.type = NoteType.GENERIC;
          element.date = new Date();
          element.by = window.appUser?.fullName;
        });
      } else data.notes = undefined;

      await Promise.all([
        saveRemoveFiles(
          undefined,
          data,
          "attachments",
          `${FilePaths.INBOUND}/${Date.now()}`,
        ),
      ]);
    } catch (err) {
      logError("inboundShipments:ValidateCreateButton:transform", err, notify);
      return Promise.reject();
    }

    // cleanup form side-effects
    delete data.customFieldSelect;
    delete data.manifestFile; // cleanup CSV upload side effects

    // Opt-in flag consumed by the dataProvider create branch; the lambda is the
    // sole writer (parent + children + inventory) via createLambdaInbound.
    data.__saveContext = {
      photos,
      account: getEffectiveAccount(data),
      selectedTimeslot,
    };

    return data;
  };

  const onSuccess = ({ data }) => {
    // The dataProvider create branch already performed the single lambda write
    // (parent + inventories + items + photos + track car + charges) via
    // createLambdaInbound. Here we only notify + redirect.
    if (data.stockImport && data.status === InboundStatus.RELEASED) {
      notify("Inbound created and completed successfully", "success");
    } else if (data.stockImport) {
      notify("Inbound created successfully. Marked as PLACED", "success");
    } else {
      notify("Inbound created successfully", "success");
    }
    // SIT railcars live on the SIT list, not the regular inbound list.
    redirect(data.sit ? SIT_LIST_PATH : "/inboundShipments");
    setIsSaving(false);
  };

  const onFailure = (error) => {
    setIsSaving(false);
    const message = error.errors[0]?.message || "";
    if (message?.startsWith("The variables input")) {
      const formState = form.getState();
      logError(
        "inboundShipments:ValidateCreateButton:data",
        formState.values,
        false,
      );
    }

    logError("inboundShipments:ValidateCreateButton:onFailure", error, notify);
  };

  // Pass ...props, not ...rest to FormDataConsumer to include handleSubmitWithRedirect from props
  return (
    <FormDataConsumer>
      {({ formData, ...props }) => (
        <>
          <Backdrop className={classes.backdrop} open={isSaving}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <SaveButton
            label="Save"
            submitOnEnter={false}
            undoable={false}
            saving={isSaving || isDisabled}
            handleSubmitWithRedirect={() => handleValidateSave(formData)}
            transform={transform}
            onSuccess={onSuccess}
            onFailure={onFailure}
          />
          <Confirm
            isOpen={open}
            loading={isSaving}
            title="Save Confirmation"
            content={confirmContent}
            onConfirm={handleDialogConfirm}
            onClose={handleDialogClose}
            CancelIcon={CancelIcon}
          />
        </>
      )}
    </FormDataConsumer>
  );
};
